import type { ProductCardItem, ProductCardsMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';

/**
 * Product cards (docs/2026-10-14-product-search-images-design.md, Phase 2): a shopping reply's pictures, each drawn
 * as a card with its row's details. Same rules as iOS's and Android's product cards.
 */

/** `★4.5 (120)`, or `★4.5` without a review count. Null without a rating. */
export function productRatingLine(item: Pick<ProductCardItem, 'rating' | 'reviews'>, locale: string): string | null {
  if (item.rating === undefined) {
    return null;
  }
  const rating = `★${item.rating.toLocaleString(locale, { maximumFractionDigits: 1 })}`;
  return item.reviews === undefined ? rating : `${rating} (${item.reviews.toLocaleString(locale)})`;
}

/** `#1 robot vacuum of 2026 — Wirecutter`, one line per expert point. A card with points draws these instead of its rating. */
export function productExpertLines(item: Pick<ProductCardItem, 'expertPoints'>): string[] {
  return (item.expertPoints ?? []).map((point) => `${point.text} — ${point.source}`);
}

/** Longest store-written value the buy message carries. */
const MAX_QUOTED_CHARS = 120;

/**
 * Every character that could end the quote or hide text: Unicode's quotation marks and opening/closing punctuation
 * (Quotation_Mark, Pi, Pf), the ornament and CJK quotes outside those sets (❛❜❝❞❟❠, 〝〞〟), primes, backticks, and
 * invisible format and control characters (zero-width, bidi overrides). The apostrophe (' and ’) stays: a title
 * like "Men's" needs it, and it cannot close a double-quoted value.
 */
const QUOTE_BREAKERS = /(?![\u0027\u2019])[\p{Quotation_Mark}\p{Pi}\p{Pf}\u275B-\u2760\u301D-\u301F\u2033\u2036`\p{Cf}\p{Cc}]/gu;

/** One line, no quote breakers, at most MAX_QUOTED_CHARS, in quotes: a store's name for its product, never a sentence. */
function quoted(value: string): string {
  const flat = value.replace(/\s+/g, ' ').replace(QUOTE_BREAKERS, '').trim();
  return `"${flat.length > MAX_QUOTED_CHARS ? `${flat.slice(0, MAX_QUOTED_CHARS - 1)}…` : flat}"`;
}

/**
 * The user message "Buy it for me" ("Book it for me" on a place) sends, the same line on every client. It is for the
 * agent, so it stays English; a missing field drops its part, and a place carries no price. The title, store and price
 * are written by the store or the listing, yet this line goes out AS the user, so each is quoted, kept to one line and
 * cut short: a store can name its product, not write the user an instruction.
 */
export function buyProductMessage(item: ProductCardItem): string {
  const link = item.productUrl ? ` ${item.productUrl}` : '';
  if (item.kind === 'place') {
    return `Book this for me: ${quoted(item.title)}${item.store ? `, ${quoted(item.store)}` : ''}.${link}`;
  }
  const from = item.store ? ` from ${quoted(item.store)}` : '';
  const price = item.price ? `, ${quoted(item.price)}` : '';
  return `Buy this for me: ${quoted(item.title)}${from}${price}.${link}`;
}

/** The attachments still drawn as files: every one whose url is not a card's picture (matched exactly). */
export function attachmentsWithoutProductPictures<T extends { url?: string }>(attachments: T[], card: ProductCardsMetadata | undefined): T[] {
  if (!card) {
    return attachments;
  }
  const pictures = new Set(card.items.map((item) => item.attachmentUrl));
  return attachments.filter((attachment) => !attachment.url || !pictures.has(attachment.url));
}
